import { useMemo } from "react";
import { FormRequiredConfig } from "@ifa/utils/src/api/types/common";

const getValueByPath = (obj: any, path: string) => {
  if (!obj || !path) return undefined;
  const keys = path.split(".");
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }
  return current;
};

const setValueByPath = (obj: any, path: string, value: any) => {
  if (!obj || !path) return obj;
  const keys = path.split(".");
  const result = { ...obj };
  let current: any = result;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (current[key] === null || current[key] === undefined || typeof current[key] !== 'object') {
      current[key] = {};
    } else {
      current[key] = { ...current[key] };
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

export const useFormConfig = (formKey: string, config: FormRequiredConfig[] | undefined, formData: any) => {
  const formConfig = useMemo(() => {
    return config?.find((c) => c.key === formKey);
  }, [config, formKey]);

  const { requiredFields, visibleFields, conditionalPaths, defaultValues } = useMemo(() => {
    const required = new Set<string>();
    const visible = new Set<string>();
    const conditional = new Set<string>();
    const basePaths = new Set<string>();
    const defaults = new Map<string, any>();

    if (!formConfig) {
      return { requiredFields: required, visibleFields: visible, conditionalPaths: conditional, defaultValues: defaults };
    }

    // 1. Base fields
    formConfig.base?.forEach((field) => {
      if (field.path) {
        basePaths.add(field.path);
        if (field.required) {
          required.add(field.path);
        }
        // 收集默认值
        if (field.defaultValue !== undefined) {
          defaults.set(field.path, field.defaultValue);
        }
      }
    });

    // 2. Collect all conditional paths (including those in base)
    formConfig.rules?.forEach((rule) => {
      rule.fields?.forEach((field) => {
        if (field.path) {
          conditional.add(field.path);
        }
      });
    });

    // 3. Evaluate rules
    formConfig.rules?.forEach((rule) => {
      if (!rule.source || !rule.type || rule.value === undefined) return;

      const sourceValue = getValueByPath(formData, rule.source);
      let match = false;

      const ruleValue = rule.value;
      const strSource = String(sourceValue === undefined || sourceValue === null ? "" : sourceValue);
      const strRule = String(ruleValue);

      switch (rule.type) {
        case "EQ":
          match = strSource === strRule;
          break;
        case "NE":
          match = strSource !== strRule;
          break;
        case "CONTAINS":
          match = strSource.includes(strRule);
          break;
        default:
          match = strSource === strRule;
      }

      if (match) {
        rule.fields?.forEach((field) => {
          if (field.path) {
            visible.add(field.path);
            if (field.required) {
              required.add(field.path);
            }
            // 收集联动规则中的默认值
            if (field.defaultValue !== undefined) {
              defaults.set(field.path, field.defaultValue);
            }
          }
        });
      }
    });

    return { requiredFields: required, visibleFields: visible, conditionalPaths: conditional, defaultValues: defaults };
  }, [formConfig, formData]);

  const isRequired = (path: string) => requiredFields.has(path);

  const isVisible = (path: string) => {
    if (!formConfig) return true;
    if (conditionalPaths.has(path)) {
      return visibleFields.has(path);
    }
    return true;
  };

  const getDefaultValue = (path: string) => defaultValues.get(path);

  // 应用默认值到数据对象
  const applyDefaultValues = (data: any) => {
    if (!data || defaultValues.size === 0) return data;

    let result = { ...data };
    defaultValues.forEach((defaultValue, path) => {
      const currentValue = getValueByPath(result, path);
      // 只在当前值为 undefined、null 或空字符串时应用默认值
      if (currentValue === undefined || currentValue === null || currentValue === '') {
        result = setValueByPath(result, path, defaultValue);
      }
    });

    return result;
  };

  return { isRequired, isVisible, getDefaultValue, applyDefaultValues, formConfig };
};
